import React, {
  FC,
  useCallback,
  useMemo,
  memo,
  useRef,
  useState,
  useEffect,
} from "react";
import styles from "./Keyboard.module.scss";
import { Key } from "./key/Key";
import { Icon, IconButton } from "@material-ui/core";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import {
  emailLayout,
  numericClearLayout,
  numericCheckLayout,
  upercaseLayout,
  completeLayout,
  lowercaseLayout,
  getStrongPasswordLayout,
} from "./keyboardLayout";

export type KeyboardType =
  | "complete"
  | "numeric"
  | "text"
  | "clear"
  | "email"
  | "strongPassword";

interface KeyboardProps {
  onKeyPress?: (keyPressed: string) => void;
  onPressEnter?: () => void;
  onPressBack?: () => void;
  onPressClear?: () => void;
  type?: KeyboardType;
  label?: string;
  onChange?: (inputValue: string) => void;
  value?: string;
  onClose?: () => void;
  hideClear?: boolean;
  disable?: boolean;
  maxLength?: number;
  hideValue?: boolean;
}

export const Keyboard: FC<KeyboardProps> = memo(
  ({
    onKeyPress,
    onPressEnter,
    onPressBack,
    onPressClear,
    type,
    onChange,
    value,
    label,
    onClose,
    hideClear,
    disable,
    maxLength,
    hideValue,
  }) => {
    const { resolution } = useScreenStore();
    const shifted = useRef(false);

    const [keyboardLayout, setKeyboardLayout] = useState(
      type === "complete"
        ? completeLayout
        : type === "strongPassword"
          ? getStrongPasswordLayout()
          : type === "numeric"
            ? numericCheckLayout
            : type === "clear"
              ? numericClearLayout
              : type === "email"
                ? emailLayout
                : type === "text"
                  ? lowercaseLayout
                  : upercaseLayout,
    );

    useEffect(() => {
      setKeyboardLayout(
        type === "complete"
          ? completeLayout
          : type === "strongPassword"
            ? getStrongPasswordLayout()
            : type === "numeric"
              ? numericCheckLayout
              : type === "clear"
                ? numericClearLayout
                : type === "email"
                  ? emailLayout
                  : type === "text"
                    ? lowercaseLayout
                    : upercaseLayout,
      );
    }, [type]);

    const onPressKey = useCallback(
      (key: string) => {
        if (key === "check") {
          if (onPressEnter) {
            onPressEnter();
          }
        } else if (key === "back-space") {
          onPressBack && onPressBack();
          if (value) {
            onChange && onChange(value.substring(0, value.length - 1));
          }
        } else if (key === "shift") {
          if (shifted.current) {
            setKeyboardLayout(lowercaseLayout);
            shifted.current = false;
          } else {
            setKeyboardLayout(upercaseLayout);
            shifted.current = true;
          }
        } else if (key === "clear") {
          onPressClear && onPressClear();
          onChange && onChange("");
        } else {
          if (value && maxLength && value.length === maxLength) {
            return;
          } else {
            onKeyPress && onKeyPress(key);
            onChange && onChange(value + key);
            if (!type || type === "text") {
              shifted.current = false;
              setKeyboardLayout(lowercaseLayout);
            }
          }
        }
      },
      [
        onPressEnter,
        onPressBack,
        value,
        onChange,
        onPressClear,
        maxLength,
        onKeyPress,
        type,
      ],
    );

    const keyboardRender = useMemo(
      () => () =>
        keyboardLayout.map((line, k) => (
          <div className={styles.keyboardLine} key={k}>
            {line.map((key, _key) => (
              <div
                key={_key}
                style={{
                  padding: type === "numeric" || type === "clear" ? 8 : 4,
                  flex:
                    (key === "check" || key === "back-space") &&
                    type !== "numeric" &&
                    type !== "clear" &&
                    type !== "strongPassword"
                      ? 2
                      : 1,
                }}
                className={styles.containerKeyboardKey}
              >
                {hideClear && key === "clear" ? (
                  <></>
                ) : (
                  <Key
                    type={
                      type === "numeric" || type === "clear"
                        ? "number"
                        : type === "strongPassword"
                          ? "password"
                          : "letter"
                    }
                    disable={disable}
                    onClick={onPressKey}
                  >
                    {key}
                  </Key>
                )}
              </div>
            ))}
          </div>
        )),
      [keyboardLayout, type, hideClear, disable, onPressKey],
    );

    return (
      <div id={styles.Keyboard}>
        {onClose && (
          <div className={styles.keyboardOnClose}>
            <IconButton onClick={onClose}>
              <Icon fontSize={"large"}>close</Icon>
            </IconButton>
          </div>
        )}
        {label && (
          <div
            className={styles.keyboardLabel}
            style={{
              minHeight: resolution === ResolutionType.FULL_HD ? 90 : 70,
            }}
          >
            {label}
          </div>
        )}
        {!hideValue && (
          <div
            className={styles.keyboardValue}
            style={{
              minHeight: resolution === ResolutionType.FULL_HD ? 80 : 60,
            }}
          >
            {value && !maxLength ? value : ""}
          </div>
        )}
        <div
          style={{
            maxHeight:
              type === "strongPassword"
                ? undefined
                : keyboardLayout.length *
                    (type === "numeric" || type === "clear"
                      ? resolution === ResolutionType.FULL_HD
                        ? 160
                        : 80
                      : 85) +
                  (hideValue ? 0 : 80),
          }}
        >
          {keyboardRender()}
        </div>
      </div>
    );
  },
);
